import { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react';
import { Download, Eye, FileText, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useDeleteOrderDocument,
  useDownloadOrderDocument,
  usePreviewOrderDocument,
  useOrderDocuments,
  useUploadOrderDocument,
} from '../../features/order-documents/api';
import { PdfPreviewDialog } from '../PdfPreviewDialog/PdfPreviewDialog';
import './OrderDocumentsPanel.scss';

interface OrderDocumentsPanelProps {
  orderId: string;
  canManage?: boolean;
}

interface PdfPreviewState {
  url: string;
  documentId: string;
  fileName: string;
}

const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;

const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
];

const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.join(',');

function formatFileSize(size?: number | null) {
  if (!size) return '—';

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(fileName: string) {
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex < 0) {
    return '';
  }

  return fileName.slice(lastDotIndex).toLowerCase();
}

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'The document must be 15 MB or smaller.';
  }

  const extension = getFileExtension(file.name);

  if (!ACCEPTED_EXTENSIONS.includes(extension)) {
    return 'This document type is not supported.';
  }

  return null;
}

export function OrderDocumentsPanel({ orderId, canManage = false }: OrderDocumentsPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const documentsQuery = useOrderDocuments(orderId);

  const uploadDocument = useUploadOrderDocument(orderId);

  const deleteDocument = useDeleteOrderDocument(orderId);

  const downloadDocument = useDownloadOrderDocument(orderId);
  const previewDocument = usePreviewOrderDocument(orderId);

  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PdfPreviewState | null>(null);
  const previewRequestRef = useRef(0);

  // If the panel switches orders or unmounts during a fetch, ignore its response.
  // PdfPreviewDialog revokes the object URL whenever its URL changes or it closes.
  useEffect(() => {
    setPreview(null);
    setPreviewingId(null);
    return () => {
      previewRequestRef.current += 1;
    };
  }, [orderId]);

  const [isDragging, setIsDragging] = useState(false);

  // Delivery tickets have their own dedicated card; keep them out of the general list.
  const documents = (documentsQuery.data?.documents ?? []).filter(
    (d: any) => d.type !== 'DELIVERY_TICKET'
  );

  const uploadFile = async (file: File) => {
    const validationError = validateFile(file);

    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      await uploadDocument.mutateAsync(file);

      toast.success('Document uploaded.');
    } catch (error: any) {
      const code = error?.response?.data?.error;

      if (code === 'FILE_TOO_LARGE') {
        toast.error('The document must be 15 MB or smaller.');
        return;
      }

      if (code === 'UNSUPPORTED_FILE_TYPE') {
        toast.error('This document type is not supported.');
        return;
      }

      toast.error(code || 'Failed to upload document.');
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = '';

    if (!file) return;

    await uploadFile(file);
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!canManage || uploadDocument.isPending) {
      return;
    }

    setIsDragging(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!canManage || uploadDocument.isPending) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }

    event.dataTransfer.dropEffect = 'copy';

    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    /*
     * Ignore dragleave events caused by moving
     * between children inside the drop area.
     */
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }

    setIsDragging(false);
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(false);

    if (!canManage || uploadDocument.isPending) {
      return;
    }

    const files = Array.from(event.dataTransfer.files ?? []);

    if (files.length === 0) {
      return;
    }

    if (files.length > 1) {
      toast.warn('Please upload one document at a time.');
      return;
    }

    await uploadFile(files[0]);
  };

  const handleDelete = async (documentId: string, fileName: string) => {
    if (!window.confirm(`Delete "${fileName}"?`)) {
      return;
    }

    try {
      await deleteDocument.mutateAsync(documentId);

      toast.success('Document deleted.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to delete document.');
    }
  };

  const handlePreview = async (documentId: string, fileName: string) => {
    if (previewingId !== null) return;

    const requestId = ++previewRequestRef.current;
    setPreviewingId(documentId);

    try {
      const pdf = await previewDocument.mutateAsync(documentId);

      // Don't open a document for an order that is no longer displayed.
      if (requestId !== previewRequestRef.current) return;

      setPreview({
        url: URL.createObjectURL(pdf),
        documentId,
        fileName,
      });
    } catch (error: any) {
      if (requestId === previewRequestRef.current) {
        toast.error(error?.response?.data?.error || error?.message || 'Failed to preview PDF.');
      }
    } finally {
      if (requestId === previewRequestRef.current) {
        setPreviewingId(null);
      }
    }
  };

  const handleDownload = async (documentId: string, fileName: string) => {
    try {
      setDownloadingId(documentId);

      await downloadDocument.mutateAsync({
        documentId,
        fileName,
      });

      toast.success('Document downloaded.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to download document.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="order-documents">
      <div className="order-documents__header">
        <div>
          <div className="order-documents__title">Order Documents</div>

          <div className="muted tiny">PDF, Word, Excel, CSV, text, or image files up to 15 MB.</div>
        </div>

        {canManage && (
          <>
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={ACCEPT_ATTRIBUTE}
              onChange={handleFileChange}
            />

            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploadDocument.isPending}
            >
              <Upload size={14} />

              {uploadDocument.isPending ? 'Uploading…' : 'Upload document'}
            </button>
          </>
        )}
      </div>

      {canManage && (
        <div
          className={[
            'order-documents__dropzone',
            isDragging ? 'order-documents__dropzone--active' : '',
            uploadDocument.isPending ? 'order-documents__dropzone--disabled' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          role="button"
          tabIndex={0}
          aria-label="Upload an order document"
          aria-disabled={uploadDocument.isPending}
          onClick={() => {
            if (!uploadDocument.isPending) {
              inputRef.current?.click();
            }
          }}
          onKeyDown={(event) => {
            if (uploadDocument.isPending) {
              return;
            }

            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();

              inputRef.current?.click();
            }
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="order-documents__dropzone-icon">
            <Upload size={24} />
          </div>

          <div className="order-documents__dropzone-content">
            <div className="order-documents__dropzone-title">
              {uploadDocument.isPending
                ? 'Uploading document…'
                : isDragging
                  ? 'Drop document here'
                  : 'Drag and drop a document here'}
            </div>

            <div className="muted tiny">
              Or click to browse your files. One document at a time, up to 15 MB.
            </div>
          </div>
        </div>
      )}

      {documentsQuery.isLoading ? (
        <div className="skeleton" style={{ height: 90 }} />
      ) : documents.length === 0 ? (
        <div className="order-documents__empty muted">No documents uploaded.</div>
      ) : (
        <div className="order-documents__list">
          {documents.map((document: any) => (
            <div key={document.id} className="order-documents__row">
              <FileText size={18} className="order-documents__icon" />

              <div className="order-documents__details">
                <div className="order-documents__name" title={document.fileName}>
                  {document.fileName}
                </div>

                <div className="muted tiny">
                  {formatFileSize(document.sizeBytes)}
                  {' · '}
                  {new Date(document.uploadedAt).toLocaleString()}

                  {document.uploadedBy?.name || document.uploadedBy?.email
                    ? ` · ${document.uploadedBy.name || document.uploadedBy.email}`
                    : ''}
                </div>
              </div>

              <div className="order-documents__actions">
                {(getFileExtension(document.fileName) === '.pdf' ||
                  document.mimeType?.toLowerCase() === 'application/pdf') && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm order-documents__preview"
                    onClick={() => handlePreview(document.id, document.fileName)}
                    disabled={previewingId !== null}
                    aria-label={`Preview ${document.fileName}`}
                  >
                    <Eye size={14} />
                    {previewingId === document.id ? 'Opening…' : 'Preview'}
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => handleDownload(document.id, document.fileName)}
                  disabled={downloadingId === document.id}
                  aria-label={`Download ${document.fileName}`}
                >
                  <Download size={14} />

                  {downloadingId === document.id ? 'Downloading…' : 'Download'}
                </button>

                {canManage && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => handleDelete(document.id, document.fileName)}
                    disabled={deleteDocument.isPending}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <PdfPreviewDialog
          url={preview.url}
          title={`PDF Preview · ${preview.fileName}`}
          onClose={() => setPreview(null)}
          footer={
            <div className="actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => handleDownload(preview.documentId, preview.fileName)}
                disabled={downloadingId === preview.documentId}
              >
                <Download size={14} />
                {downloadingId === preview.documentId ? 'Downloading…' : 'Download PDF'}
              </button>
            </div>
          }
        />
      )}
    </div>
  );
}
